<template>

    <Grid v-if="showGrid" class="debugger pointer-events-none w-screen h-screen fixed left-0 top-0 right-0">

        <template #default="{ columns }">

            <Column v-for="index of columns" :key="index" :span="1" class="debugger__column">

                <div class="h-screen w-full"/>

            </Column>

            <div class="debugger__grid-size"/>

        </template>

    </Grid>

</template>

<script lang="ts">

    import { defineComponent, onUnmounted } from 'vue'
    import Column from './Grid/Column.vue'
    import Grid from './Grid/Grid.vue'
    import { useGrid } from '../compositions/UseGrid'

    export default defineComponent({
        name: 'Debugger',
        components: { Grid, Column },
        setup() {

            const { toggleGrid, showGrid } = useGrid()

            const toggleDebugger = (event: KeyboardEvent) => {

                const target = event.target

                if (target instanceof HTMLInputElement || (target instanceof HTMLDivElement && target.contentEditable === 'true')) {

                    return

                }

                /**
                 * Only toggle debugger when control is held up
                 */
                if (event.ctrlKey === false) {

                    return

                }

                if (event.key === 'g') {

                    toggleGrid()

                } else if (event.key === 'i') {

                    document.body.classList.toggle('pesticide')

                } else if (event.key === 'x') {

                    document.body.classList.toggle('x-ray')

                }

            }

            window.addEventListener('keydown', toggleDebugger)
            onUnmounted(() => window.removeEventListener('keydown', toggleDebugger))

            return {
                showGrid,
                maxHeight: window.innerHeight
            }

        }

    })

</script>

<style lang="scss">

    @include component(debugger) {

        z-index: 100;
        background-color: rgba(0, 212, 255, .15);

        * {

            outline: 1px solid transparent !important;

        }

        @include part(grid-size) {

            position: absolute;

            &:before {

                position: fixed;
                top: 0;
                left: 0;
                margin: 0;
                padding: 5px;
                font-weight: bold;
                font-size: 10px;
                text-transform: uppercase;
                background: rgba(0, 212, 255, .25);

            }

            @screen sm {

                &:before {

                    content: 'sm';

                }

            }

            @screen md {

                &:before {

                    content: 'md';

                }

            }

            @screen lg {

                &:before {

                    content: 'lg';

                }

            }

            @screen xl {

                &:before {

                    content: 'xl';

                }

            }

        }

        @include part(column) {

            outline: 0.0625rem solid rgba(0, 212, 255, .25) !important;

            > div {

                background-color: rgba(0, 212, 255, .25) !important;

            }

        }

    }

    @include component(pesticide) {

        $pesticide-debug-depth: false;

        @import "~pesticide/sass/pesticide";

    }

    @include component(x-ray) {

        * {

            color: #444444 !important;
            background: transparent !important;

            &:before, &:after {

                color: #444444 !important;
                background: transparent !important;

            }

        }

        %base {

            position: relative;
            transition: color 150ms;

            &:hover {

                color: red !important;
                background: white !important;
                outline: 1px solid red !important;

                &:before {

                    color: red !important;
                    background: white !important;
                    outline: 1px solid red;

                }

            }

            &:before {

                position: absolute;
                top: 0;
                left: -16px;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 1.5em;
                height: 1.5em;
                padding: 2px;
                color: black !important;
                font-size: 10px;
                font-family: monospace;
                line-height: 1;
                text-transform: uppercase;
                outline: 1px solid black;

            }

        }

        @for $i from 1 through 6 {

            h#{$i} {

                @extend %base;

                &:before {

                    content: 'h#{$i}';

                }

            }

        }

        p {

            @extend %base;

            &:before {

                content: 'p';

            }

        }

        a {

            @extend %base;

            &:before {

                content: 'a';

            }

        }

        p > *:not(span):not(b):not(a) {

            &:before {

                position: absolute;
                display: flex;
                align-content: center;
                align-items: center;
                justify-content: center;
                width: 100%;
                height: 100%;
                color: red !important;
                font-weight: bold;
                font-size: 20px;
                text-transform: uppercase;
                outline: 2px solid red;
                content: 'invalid';

            }

        }

        img, video, svg {

            filter: invert(.5);

        }

        svg path[stroke^="#fff"] {

            stroke: #444444 !important;

        }

        *:not(path):not(.debugger):not(.debugger__column) {

            outline: 1px solid;

        }

    }

</style>
